import * as THREE from 'three';

// Interfaces de datos railway (copiadas de tu componente principal)
export interface Hotspot {
  id?: number;
  location: string;
  defect_count: number;
  risk_score: number;
  recommendation_priority: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  dominant_defects?: string[];
  severity_breakdown?: { [key: string]: number };
  maintenance_history?: string[];
}

export interface AnalysisData {
  recommendations?: string[];
  location_range?: {
    kp_start: number;
    kp_end: number;
  };
  total_defects?: number;
  defect_density?: number;
  risk_hotspots?: any[];
  failure_prediction?: {
    risk_level: string;
  };
}

// Interfaces para datos 3D
export interface Point3D {
  x: number;
  y: number;
  z: number;
  color: THREE.Color;
  size: number;
  metadata: any;
  label: string;
}

export interface Chart3DData {
  points: Point3D[];
  axes: {
    x: { label: string; min: number; max: number; };
    y: { label: string; min: number; max: number; };
    z: { label: string; min: number; max: number; };
  };
  bounds: {
    minX: number; maxX: number;
    minY: number; maxY: number;
    minZ: number; maxZ: number;
  };
}

export class RailwayDataService {
  
  // Mapeo de colores según prioridad
  private static readonly PRIORITY_COLORS = {
    'CRITICAL': new THREE.Color(0xdc3545), // Rojo
    'HIGH': new THREE.Color(0xfd7e14),     // Naranja
    'MEDIUM': new THREE.Color(0xffc107),   // Amarillo
    'LOW': new THREE.Color(0x198754),      // Verde
    'UNKNOWN': new THREE.Color(0x6c757d)   // Gris
  };

  // Mapeo de colores para métricas técnicas
  private static readonly METRIC_COLORS = {
    'geometry': new THREE.Color(0x0066cc),
    'ai_risk': new THREE.Color(0xff0f00),
    'wear': new THREE.Color(0x28a745),
    'dynamic': new THREE.Color(0x6f42c1),
    'fatigue': new THREE.Color(0xfd7e14),
    'compliance': new THREE.Color(0xc6b502)
  };

  /**
   * Convierte hotspots en datos 3D para bubble chart - VERSIÓN CORREGIDA
   */
  public static convertHotspotsTo3D(hotspots: Hotspot[]): Chart3DData {
    console.log('🔄 Converting hotspots to 3D:', { count: hotspots?.length || 0 });

    // CORRECCIÓN 1: Validación robusta de entrada
    if (!hotspots || hotspots.length === 0) {
      console.log('⚠️ No hotspots provided, generating demo data');
      return this.generateDemoHotspotsData();
    }

    // CORRECCIÓN 2: Intentar primero con coordenadas reales
    const validHotspots = hotspots.filter(h => 
      h.coordinates && 
      !isNaN(h.coordinates.latitude) && 
      !isNaN(h.coordinates.longitude) &&
      h.coordinates.latitude !== 0 &&
      h.coordinates.longitude !== 0
    );

    console.log('📍 Valid hotspots with coordinates:', validHotspots.length);

    if (validHotspots.length === 0) {
      console.log('🎲 No valid coordinates, generating positioned mock data');
      return this.generatePositionedMockData(hotspots);
    }

    // CORRECCIÓN 3: Cálculo de rangos con validación
    const points: Point3D[] = [];
    let minLat = validHotspots[0].coordinates!.latitude;
    let maxLat = validHotspots[0].coordinates!.latitude;
    let minLng = validHotspots[0].coordinates!.longitude;
    let maxLng = validHotspots[0].coordinates!.longitude;
    let minRisk = validHotspots[0].risk_score || 0;
    let maxRisk = validHotspots[0].risk_score || 1;

    // Calcular rangos reales
    validHotspots.forEach(hotspot => {
      if (hotspot.coordinates) {
        minLat = Math.min(minLat, hotspot.coordinates.latitude);
        maxLat = Math.max(maxLat, hotspot.coordinates.latitude);
        minLng = Math.min(minLng, hotspot.coordinates.longitude);
        maxLng = Math.max(maxLng, hotspot.coordinates.longitude);
      }
      const risk = hotspot.risk_score || 0;
      minRisk = Math.min(minRisk, risk);
      maxRisk = Math.max(maxRisk, risk);
    });

    console.log('📊 Calculated ranges:', { minLat, maxLat, minLng, maxLng, minRisk, maxRisk });

    // CORRECCIÓN 4: Manejar casos donde min === max
    const latRange = maxLat - minLat || 1;
    const lngRange = maxLng - minLng || 1;
    const riskRange = maxRisk - minRisk || 1;

    // Crear puntos 3D
    validHotspots.forEach((hotspot, index) => {
      if (!hotspot.coordinates) return;

      // Normalizar con rangos seguros
      const x = ((hotspot.coordinates.latitude - minLat) / latRange) * 80 + 10;
      const y = ((hotspot.risk_score || 0) - minRisk) / riskRange * 80 + 10;
      const z = ((hotspot.coordinates.longitude - minLng) / lngRange) * 80 + 10;

      // Validar que los valores son números finitos
      if (!isFinite(x) || !isFinite(y) || !isFinite(z)) {
        console.warn('⚠️ Invalid coordinates for hotspot:', hotspot.location, { x, y, z });
        return;
      }

      const size = Math.max(2, Math.min(8, (hotspot.defect_count || 1) * 0.3 + 2));
      const color = this.PRIORITY_COLORS[hotspot.recommendation_priority] || 
                   this.PRIORITY_COLORS['UNKNOWN'];

      points.push({
        x, y, z,
        color: color.clone(),
        size,
        metadata: {
          ...hotspot,
          originalIndex: index,
          type: 'hotspot'
        },
        label: `${hotspot.location} (${hotspot.defect_count || 0} defectos)`
      });
    });

    console.log('✅ Generated 3D points:', points.length);

    return {
      points,
      axes: {
        x: { label: 'Latitud', min: 0, max: 100 },
        y: { label: 'Risk Score', min: 0, max: 100 },
        z: { label: 'Longitud', min: 0, max: 100 }
      },
      bounds: {
        minX: 0, maxX: 100,
        minY: 0, maxY: 100,
        minZ: 0, maxZ: 100
      }
    };
  }

  /**
   * NUEVA FUNCIÓN: Genera datos posicionados de forma inteligente
   */
  private static generatePositionedMockData(hotspots: Hotspot[]): Chart3DData {
    console.log('🎯 Generating positioned mock data for', hotspots.length, 'hotspots');
    
    const points: Point3D[] = [];
    const limitedHotspots = hotspots.slice(0, 50); // Limitar para evitar sobrecarga
    
    limitedHotspots.forEach((hotspot, index) => {
      // ESTRATEGIA 1: Grid inteligente con dispersión
      const gridSize = Math.ceil(Math.sqrt(limitedHotspots.length));
      const row = Math.floor(index / gridSize);
      const col = index % gridSize;
      
      // Posición base en grid
      const baseX = (col / Math.max(1, gridSize - 1)) * 60 + 20;
      const baseZ = (row / Math.max(1, gridSize - 1)) * 60 + 20;
      
      // Añadir variación basada en propiedades del hotspot
      const riskVariation = (hotspot.risk_score || 0.5) * 20 - 10;
      const defectVariation = Math.min(10, (hotspot.defect_count || 1) * 2);
      
      const x = baseX + riskVariation + (Math.random() - 0.5) * 8;
      const y = (hotspot.risk_score || Math.random() * 0.8 + 0.1) * 90 + 5;
      const z = baseZ + defectVariation + (Math.random() - 0.5) * 8;

      // Asegurar que están dentro de los límites
      const clampedX = Math.max(5, Math.min(95, x));
      const clampedY = Math.max(5, Math.min(95, y));
      const clampedZ = Math.max(5, Math.min(95, z));

      const size = Math.max(2, Math.min(8, (hotspot.defect_count || 1) * 0.4 + 2));
      const color = this.PRIORITY_COLORS[hotspot.recommendation_priority] || 
                   this.PRIORITY_COLORS['UNKNOWN'];

      points.push({
        x: clampedX,
        y: clampedY, 
        z: clampedZ,
        color: color.clone(),
        size,
        metadata: {
          ...hotspot,
          originalIndex: index,
          type: 'hotspot',
          isPositionedMock: true
        },
        label: `${hotspot.location} (${hotspot.defect_count || 0} defectos) [Mock]`
      });
    });

    console.log('✅ Generated positioned mock points:', points.length);

    return {
      points,
      axes: {
        x: { label: 'Distribución Inteligente X', min: 0, max: 100 },
        y: { label: 'Risk Score', min: 0, max: 100 },
        z: { label: 'Distribución Inteligente Z', min: 0, max: 100 }
      },
      bounds: {
        minX: 0, maxX: 100,
        minY: 0, maxY: 100,
        minZ: 0, maxZ: 100
      }
    };
  }

  /**
   * NUEVA FUNCIÓN: Genera datos demo cuando no hay nada
   */
  private static generateDemoHotspotsData(): Chart3DData {
    console.log('🎮 Generating demo data - no hotspots available');
    
    const demoHotspots: Hotspot[] = [
      {
        location: 'Demo KP 10.5',
        defect_count: 15,
        risk_score: 0.85,
        recommendation_priority: 'CRITICAL'
      },
      {
        location: 'Demo KP 23.2',
        defect_count: 8,
        risk_score: 0.65,
        recommendation_priority: 'HIGH'
      },
      {
        location: 'Demo KP 35.7',
        defect_count: 4,
        risk_score: 0.35,
        recommendation_priority: 'MEDIUM'
      },
      {
        location: 'Demo KP 47.1',
        defect_count: 2,
        risk_score: 0.15,
        recommendation_priority: 'LOW'
      },
      {
        location: 'Demo KP 58.9',
        defect_count: 12,
        risk_score: 0.75,
        recommendation_priority: 'HIGH'
      },
      {
        location: 'Demo KP 67.3',
        defect_count: 6,
        risk_score: 0.45,
        recommendation_priority: 'MEDIUM'
      }
    ];

    return this.generatePositionedMockData(demoHotspots);
  }

  /**
   * FUNCIÓN CORREGIDA: convertAnalysisTo3D con mejor validación
   */
  public static convertAnalysisTo3D(
    analysisData: AnalysisData | null, 
    hotspots: Hotspot[],
    selectedMetrics: string[] = ['geometry', 'ai_risk', 'wear']
  ): Chart3DData {
    
    console.log('🔄 Converting analysis to 3D:', { 
      hasAnalysis: !!analysisData, 
      hotspotCount: hotspots?.length || 0,
      metrics: selectedMetrics 
    });

    // Generar datos técnicos
    const chartData = this.generateTechnicalChartData(analysisData, hotspots);
    
    if (chartData.length === 0) {
      console.log('⚠️ No technical data generated, creating demo technical data');
      return this.generateDemoTechnicalData(selectedMetrics);
    }

    const points: Point3D[] = [];
    
    // CORRECCIÓN: Calcular rangos con validación
    let minKP = parseFloat(chartData[0].kp);
    let maxKP = parseFloat(chartData[0].kp);
    let minValue = Infinity;
    let maxValue = -Infinity;

    chartData.forEach(item => {
      const kp = parseFloat(item.kp);
      if (isFinite(kp)) {
        minKP = Math.min(minKP, kp);
        maxKP = Math.max(maxKP, kp);
      }

      selectedMetrics.forEach(metric => {
        const value = item[metric] || 0;
        if (isFinite(value)) {
          minValue = Math.min(minValue, value);
          maxValue = Math.max(maxValue, value);
        }
      });
    });

    // Manejar casos extremos
    const kpRange = maxKP - minKP || 1;
    const valueRange = maxValue - minValue || 1;

    console.log('📊 Technical data ranges:', { minKP, maxKP, minValue, maxValue });

    // Crear puntos 3D para cada métrica
    chartData.forEach((item, itemIndex) => {
      const kp = parseFloat(item.kp);
      if (!isFinite(kp)) return;

      const normalizedKP = ((kp - minKP) / kpRange) * 80 + 10;

      selectedMetrics.forEach((metric, metricIndex) => {
        const value = item[metric] || 0;
        if (!isFinite(value)) return;

        const normalizedValue = ((value - minValue) / valueRange) * 80 + 10;
        
        // Posición Z basada en métrica (separar métricas)
        const z = (metricIndex / Math.max(1, selectedMetrics.length - 1)) * 60 + 20;

        const color = this.METRIC_COLORS[metric] || new THREE.Color(0x888888);
        const size = Math.max(1.5, Math.min(6, (normalizedValue / 100) * 4 + 2));

        points.push({
          x: normalizedKP,
          y: normalizedValue,
          z,
          color: color.clone(),
          size,
          metadata: {
            ...item,
            metric,
            originalValue: value,
            kp: item.kp,
            type: 'technical'
          },
          label: `KP ${item.kp} - ${metric}: ${value.toFixed(2)}`
        });
      });
    });

    console.log('✅ Generated technical 3D points:', points.length);

    return {
      points,
      axes: {
        x: { label: 'Kilómetro Punto (KP)', min: 0, max: 100 },
        y: { label: 'Valores Normalizados', min: 0, max: 100 },
        z: { label: 'Métricas', min: 0, max: 100 }
      },
      bounds: {
        minX: 0, maxX: 100,
        minY: 0, maxY: 100,
        minZ: 0, maxZ: 100
      }
    };
  }

  /**
   * NUEVA FUNCIÓN: Genera datos técnicos demo
   */
  private static generateDemoTechnicalData(selectedMetrics: string[]): Chart3DData {
    console.log('🎮 Generating demo technical data for metrics:', selectedMetrics);
    
    const points: Point3D[] = [];
    const kpPoints = 15;
    
    for (let i = 0; i < kpPoints; i++) {
      const kp = i * 3; // KP 0, 3, 6, 9, etc.
      const normalizedKP = (i / (kpPoints - 1)) * 80 + 10;
      
      selectedMetrics.forEach((metric, metricIndex) => {
        let value = 0;
        
        // Generar valores demo específicos por métrica
        switch (metric) {
          case 'geometry':
            value = 10 + Math.sin(i * 0.5) * 5 + Math.random() * 3;
            break;
          case 'ai_risk':
            value = 0.3 + Math.sin(i * 0.3) * 0.2 + Math.random() * 0.1;
            break;
          case 'wear':
            value = 2 + Math.cos(i * 0.4) * 1 + Math.random() * 0.5;
            break;
          case 'dynamic':
            value = 100 + Math.sin(i * 0.6) * 20 + Math.random() * 10;
            break;
          case 'fatigue':
            value = 80 + Math.cos(i * 0.2) * 10 + Math.random() * 5;
            break;
          case 'compliance':
            value = 85 + Math.sin(i * 0.4) * 10 + Math.random() * 5;
            break;
          default:
            value = 50 + Math.random() * 50;
        }

        // Normalizar valor (0-100 range aproximadamente)
        const normalizedValue = Math.max(5, Math.min(95, (value / 150) * 100));
        
        // Posición Z basada en métrica
        const z = (metricIndex / Math.max(1, selectedMetrics.length - 1)) * 60 + 20;

        const color = this.METRIC_COLORS[metric] || new THREE.Color(0x888888);
        const size = Math.max(1.5, Math.min(6, (normalizedValue / 100) * 4 + 2));

        points.push({
          x: normalizedKP,
          y: normalizedValue,
          z,
          color: color.clone(),
          size,
          metadata: {
            kp: kp.toFixed(1),
            metric,
            originalValue: value,
            type: 'technical',
            isDemoData: true
          },
          label: `Demo KP ${kp.toFixed(1)} - ${metric}: ${value.toFixed(2)}`
        });
      });
    }

    console.log('✅ Generated demo technical points:', points.length);

    return {
      points,
      axes: {
        x: { label: 'Kilómetro Punto (KP)', min: 0, max: 100 },
        y: { label: 'Valores Demo', min: 0, max: 100 },
        z: { label: 'Métricas', min: 0, max: 100 }
      },
      bounds: {
        minX: 0, maxX: 100,
        minY: 0, maxY: 100,
        minZ: 0, maxZ: 100
      }
    };
  }

  /**
   * FUNCIÓN MEJORADA: generateTechnicalChartData con mejor logging
   */
  private static generateTechnicalChartData(
    analysisData: AnalysisData | null, 
    hotspots: Hotspot[]
  ): any[] {
    console.log('🔧 Generating technical chart data:', { 
      hasAnalysis: !!analysisData, 
      hasLocationRange: !!(analysisData?.location_range),
      hotspotCount: hotspots?.length || 0 
    });

    if (analysisData && analysisData.location_range) {
      const kpStart = analysisData.location_range.kp_start || 0;
      const kpEnd = analysisData.location_range.kp_end || 50;
      const range = kpEnd - kpStart;
      const points = Math.min(20, Math.max(10, range));
      
      console.log('📍 Using analysis data range:', { kpStart, kpEnd, range, points });
      
      const data = Array.from({ length: points }, (_, index) => {
        const kp = kpStart + (index * range) / (points - 1);
        const baseRisk = Math.random() * 0.5 + 0.3;
        
        return {
          kp: kp.toFixed(1),
          geometry: 15 + Math.sin(index * 0.5) * 10 + Math.random() * 5,
          ai_risk: baseRisk + Math.sin(index * 0.3) * 0.2,
          wear: 2.5 + Math.cos(index * 0.4) * 1.2 + Math.random() * 0.5,
          dynamic: 120 + Math.sin(index * 0.6) * 30 + Math.random() * 10,
          fatigue: 85 + Math.cos(index * 0.2) * 15 + Math.random() * 8,
          compliance: Math.max(60, 95 - Math.abs(Math.sin(index * 0.4)) * 25)
        };
      });

      console.log('✅ Generated from analysis data:', data.length, 'points');
      return data;
    }

    // Fallback usando hotspots
    if (hotspots && hotspots.length > 0) {
      console.log('📊 Using hotspots fallback');
      
      const data = hotspots.slice(0, 15).map((hotspot, index) => {
        const kpMatch = hotspot.location.match(/(\d+\.?\d*)/);
        const kp = kpMatch ? parseFloat(kpMatch[1]) : index * 5;
        const riskMultiplier = hotspot.recommendation_priority === 'CRITICAL' ? 1.5 : 
                              hotspot.recommendation_priority === 'HIGH' ? 1.2 : 1.0;
        
        return {
          kp: kp.toFixed(1),
          location: hotspot.location,
          geometry: (hotspot.risk_score || 0.5) * 30 * riskMultiplier,
          ai_risk: (hotspot.risk_score || 0.5) * riskMultiplier,
          wear: (hotspot.defect_count || 1) * 0.5 + Math.random() * 2,
          dynamic: 100 + (hotspot.risk_score || 0.5) * 50 * riskMultiplier,
          fatigue: Math.max(40, 100 - (hotspot.risk_score || 0.5) * 60 * riskMultiplier),
          compliance: Math.max(50, 95 - (hotspot.risk_score || 0.5) * 45 * riskMultiplier),
          priority: hotspot.recommendation_priority,
          defects: hotspot.defect_count
        };
      }).sort((a, b) => parseFloat(a.kp) - parseFloat(b.kp));

      console.log('✅ Generated from hotspots:', data.length, 'points');
      return data;
    }

    console.log('⚠️ No data source available for technical chart');
    return [];
  }

  /**
   * Crea datos vacíos
   */
  private static getEmptyChart3DData(): Chart3DData {
    console.log('🗑️ Returning empty chart data');
    return {
      points: [],
      axes: {
        x: { label: 'X', min: 0, max: 100 },
        y: { label: 'Y', min: 0, max: 100 },
        z: { label: 'Z', min: 0, max: 100 }
      },
      bounds: {
        minX: 0, maxX: 100,
        minY: 0, maxY: 100,
        minZ: 0, maxZ: 100
      }
    };
  }

  /**
   * Normaliza un valor entre un rango mín/máx a un nuevo rango
   */
  private static normalize(
    value: number, 
    oldMin: number, 
    oldMax: number, 
    newMin: number, 
    newMax: number
  ): number {
    if (oldMax === oldMin) return newMin;
    return newMin + (value - oldMin) * (newMax - newMin) / (oldMax - oldMin);
  }

  /**
   * Obtiene configuración de colores para leyenda
   */
  public static getPriorityColorMap(): { [key: string]: string } {
    return {
      'CRITICAL': '#dc3545',
      'HIGH': '#fd7e14',
      'MEDIUM': '#ffc107',
      'LOW': '#198754',
      'UNKNOWN': '#6c757d'
    };
  }

  /**
   * Obtiene configuración de colores para métricas
   */
  public static getMetricColorMap(): { [key: string]: string } {
    return {
      'geometry': '#0066cc',
      'ai_risk': '#ff0f00',
      'wear': '#28a745',
      'dynamic': '#6f42c1',
      'fatigue': '#fd7e14',
      'compliance': '#c6b502'
    };
  }

  /**
   * Convierte THREE.Color a string hexadecimal
   */
  public static colorToHex(color: THREE.Color): string {
    return `#${color.getHexString()}`;
  }

  /**
   * Obtiene estadísticas de los datos 3D
   */
  public static getDataStatistics(data: Chart3DData): {
    totalPoints: number;
    priorityDistribution: { [key: string]: number };
    averageRiskScore: number;
    maxDefectCount: number;
  } {
    const stats = {
      totalPoints: data.points.length,
      priorityDistribution: {} as { [key: string]: number },
      averageRiskScore: 0,
      maxDefectCount: 0
    };

    if (data.points.length === 0) return stats;

    let totalRisk = 0;
    data.points.forEach(point => {
      // Distribución de prioridades
      const priority = point.metadata.recommendation_priority || 'UNKNOWN';
      stats.priorityDistribution[priority] = (stats.priorityDistribution[priority] || 0) + 1;

      // Risk score promedio
      totalRisk += point.metadata.risk_score || 0;

      // Máximo defect count
      stats.maxDefectCount = Math.max(stats.maxDefectCount, point.metadata.defect_count || 0);
    });

    stats.averageRiskScore = totalRisk / data.points.length;

    return stats;
  }

  /**
   * NUEVA FUNCIÓN: Valida la estructura de datos 3D
   */
  public static validateChart3DData(data: Chart3DData): {
    isValid: boolean;
    errors: string[];
    warnings: string[];
  } {
    const result = {
      isValid: true,
      errors: [] as string[],
      warnings: [] as string[]
    };

    if (!data) {
      result.isValid = false;
      result.errors.push('Data is null or undefined');
      return result;
    }

    if (!data.points || !Array.isArray(data.points)) {
      result.isValid = false;
      result.errors.push('Points array is missing or invalid');
      return result;
    }

    if (data.points.length === 0) {
      result.warnings.push('Points array is empty');
    }

    // Validar cada punto
    data.points.forEach((point, index) => {
      if (!isFinite(point.x) || !isFinite(point.y) || !isFinite(point.z)) {
        result.errors.push(`Point ${index} has invalid coordinates: (${point.x}, ${point.y}, ${point.z})`);
        result.isValid = false;
      }

      if (!point.color || typeof point.color.getHex !== 'function') {
        result.errors.push(`Point ${index} has invalid color`);
        result.isValid = false;
      }

      if (!point.metadata) {
        result.warnings.push(`Point ${index} has no metadata`);
      }
    });

    console.log('🔍 Data validation result:', result);
    return result;
  }

  /**
   * NUEVA FUNCIÓN: Obtiene información de debug
   */
  public static getDebugInfo(data: Chart3DData): any {
    const info = {
      totalPoints: data.points.length,
      pointTypes: {} as { [key: string]: number },
      coordinateRanges: {
        x: { min: Infinity, max: -Infinity },
        y: { min: Infinity, max: -Infinity },
        z: { min: Infinity, max: -Infinity }
      },
      sizes: [] as number[],
      colors: [] as string[]
    };

    data.points.forEach(point => {
      // Tipos de puntos
      const type = point.metadata?.type || 'unknown';
      info.pointTypes[type] = (info.pointTypes[type] || 0) + 1;

      // Rangos de coordenadas
      info.coordinateRanges.x.min = Math.min(info.coordinateRanges.x.min, point.x);
      info.coordinateRanges.x.max = Math.max(info.coordinateRanges.x.max, point.x);
      info.coordinateRanges.y.min = Math.min(info.coordinateRanges.y.min, point.y);
      info.coordinateRanges.y.max = Math.max(info.coordinateRanges.y.max, point.y);
      info.coordinateRanges.z.min = Math.min(info.coordinateRanges.z.min, point.z);
      info.coordinateRanges.z.max = Math.max(info.coordinateRanges.z.max, point.z);

      // Tamaños
      info.sizes.push(point.size);

      // Colores únicos
      const colorHex = this.colorToHex(point.color);
      if (!info.colors.includes(colorHex)) {
        info.colors.push(colorHex);
      }
    });

    console.log('🐛 Debug info:', info);
    return info;
  }

  /**
   * NUEVA FUNCIÓN: Combina datos de hotspots y técnicos para vista combinada
   */
  public static convertCombinedTo3D(
    hotspots: Hotspot[],
    analysisData: AnalysisData | null,
    selectedMetrics: string[] = ['geometry', 'ai_risk', 'wear']
  ): Chart3DData {
    console.log('🔄 Converting combined data to 3D:', { 
      hotspotCount: hotspots?.length || 0,
      hasAnalysis: !!analysisData,
      metrics: selectedMetrics 
    });

    // Obtener datos de hotspots
    const hotspotsData = this.convertHotspotsTo3D(hotspots);
    
    // Obtener datos técnicos
    const technicalData = this.convertAnalysisTo3D(analysisData, hotspots, selectedMetrics);

    // Combinar puntos
    const combinedPoints: Point3D[] = [];

    // Añadir hotspots en la mitad izquierda del espacio (X: 0-45)
    hotspotsData.points.forEach(point => {
      combinedPoints.push({
        ...point,
        x: point.x * 0.45, // Comprimir a la mitad izquierda
        metadata: {
          ...point.metadata,
          section: 'hotspots'
        }
      });
    });

    // Añadir datos técnicos en la mitad derecha del espacio (X: 55-100)
    technicalData.points.forEach(point => {
      combinedPoints.push({
        ...point,
        x: point.x * 0.45 + 55, // Comprimir y mover a la mitad derecha
        metadata: {
          ...point.metadata,
          section: 'technical'
        }
      });
    });

    console.log('✅ Generated combined 3D points:', {
      total: combinedPoints.length,
      hotspots: hotspotsData.points.length,
      technical: technicalData.points.length
    });

    return {
      points: combinedPoints,
      axes: {
        x: { label: 'Hotspots ← → Técnico', min: 0, max: 100 },
        y: { label: 'Risk Score / Valores', min: 0, max: 100 },
        z: { label: 'Distribución / Métricas', min: 0, max: 100 }
      },
      bounds: {
        minX: 0, maxX: 100,
        minY: 0, maxY: 100,
        minZ: 0, maxZ: 100
      }
    };
  }

  /**
   * NUEVA FUNCIÓN: Aplica filtros a los datos 3D
   */
  public static applyFilters(
    data: Chart3DData,
    filters: {
      priorities?: string[];
      riskRange?: [number, number];
      defectCountRange?: [number, number];
      kpRange?: [number, number];
      selectedMetrics?: string[];
    }
  ): Chart3DData {
    console.log('🔍 Applying filters:', filters);

    if (!data || data.points.length === 0) {
      return data;
    }

    const filteredPoints = data.points.filter(point => {
      // Filtro de prioridades
      if (filters.priorities && filters.priorities.length > 0) {
        const priority = point.metadata.recommendation_priority;
        if (priority && !filters.priorities.includes(priority)) {
          return false;
        }
      }

      // Filtro de risk score
      if (filters.riskRange) {
        const risk = point.metadata.risk_score || 0;
        if (risk < filters.riskRange[0] || risk > filters.riskRange[1]) {
          return false;
        }
      }

      // Filtro de defect count
      if (filters.defectCountRange) {
        const defects = point.metadata.defect_count || 0;
        if (defects < filters.defectCountRange[0] || defects > filters.defectCountRange[1]) {
          return false;
        }
      }

      // Filtro de KP range
      if (filters.kpRange && point.metadata.kp) {
        const kp = parseFloat(point.metadata.kp);
        if (isFinite(kp) && (kp < filters.kpRange[0] || kp > filters.kpRange[1])) {
          return false;
        }
      }

      // Filtro de métricas seleccionadas
      if (filters.selectedMetrics && filters.selectedMetrics.length > 0) {
        const metric = point.metadata.metric;
        if (metric && !filters.selectedMetrics.includes(metric)) {
          return false;
        }
      }

      return true;
    });

    console.log('✅ Filtered points:', {
      original: data.points.length,
      filtered: filteredPoints.length,
      removed: data.points.length - filteredPoints.length
    });

    return {
      ...data,
      points: filteredPoints
    };
  }

  /**
   * NUEVA FUNCIÓN: Limita el número de hotspots
   */
  public static limitHotspots(
    hotspots: Hotspot[],
    limit: '1-50' | '51-150' | '151-250' | '251-500' | 'unlimited'
  ): Hotspot[] {
    if (!hotspots || hotspots.length === 0) {
      return hotspots;
    }

    let maxHotspots: number;
    switch (limit) {
      case '1-50':
        maxHotspots = 50;
        break;
      case '51-150':
        maxHotspots = 150;
        break;
      case '151-250':
        maxHotspots = 250;
        break;
      case '251-500':
        maxHotspots = 500;
        break;
      case 'unlimited':
        return hotspots;
      default:
        maxHotspots = 50;
    }

    const limited = hotspots.slice(0, maxHotspots);
    
    console.log('🎯 Limited hotspots:', {
      original: hotspots.length,
      limit: maxHotspots,
      final: limited.length
    });

    return limited;
  }

  /**
   * NUEVA FUNCIÓN: Obtiene métricas disponibles de los datos
   */
  public static getAvailableMetrics(
    analysisData: AnalysisData | null,
    hotspots: Hotspot[]
  ): string[] {
    console.log('📊 Getting available metrics');

    const chartData = this.generateTechnicalChartData(analysisData, hotspots);
    
    if (chartData.length === 0) {
      return Object.keys(this.METRIC_COLORS);
    }

    // Obtener métricas que tienen datos
    const availableMetrics = new Set<string>();
    
    chartData.forEach(item => {
      Object.keys(item).forEach(key => {
        if (key !== 'kp' && key !== 'location' && key !== 'priority' && key !== 'defects') {
          if (this.METRIC_COLORS[key]) {
            availableMetrics.add(key);
          }
        }
      });
    });

    const metrics = Array.from(availableMetrics);
    console.log('✅ Available metrics:', metrics);
    
    return metrics.length > 0 ? metrics : Object.keys(this.METRIC_COLORS);
  }

  /**
   * NUEVA FUNCIÓN: Calcula calidad de los datos
   */
  public static calculateDataQuality(
    hotspots: Hotspot[],
    analysisData: AnalysisData | null
  ): 'excellent' | 'good' | 'poor' | 'empty' {
    if (!hotspots || hotspots.length === 0) {
      return 'empty';
    }

    let score = 0;
    const totalPossible = 100;

    // Puntuación por cantidad de hotspots
    if (hotspots.length >= 50) score += 25;
    else if (hotspots.length >= 20) score += 20;
    else if (hotspots.length >= 10) score += 15;
    else if (hotspots.length >= 5) score += 10;

    // Puntuación por coordenadas válidas
    const validCoords = hotspots.filter(h => 
      h.coordinates && 
      !isNaN(h.coordinates.latitude) && 
      !isNaN(h.coordinates.longitude) &&
      h.coordinates.latitude !== 0 &&
      h.coordinates.longitude !== 0
    ).length;
    
    const coordPercentage = validCoords / hotspots.length;
    score += coordPercentage * 25;

    // Puntuación por completitud de datos
    const completeData = hotspots.filter(h => 
      h.risk_score !== undefined &&
      h.defect_count !== undefined &&
      h.recommendation_priority &&
      h.location
    ).length;
    
    const dataPercentage = completeData / hotspots.length;
    score += dataPercentage * 25;

    // Puntuación por datos de análisis
    if (analysisData) {
      if (analysisData.location_range) score += 10;
      if (analysisData.recommendations && analysisData.recommendations.length > 0) score += 5;
      if (analysisData.risk_hotspots && analysisData.risk_hotspots.length > 0) score += 5;
      if (analysisData.failure_prediction) score += 5;
    }

    console.log('📈 Data quality score:', { score, totalPossible, percentage: (score / totalPossible * 100).toFixed(1) });

    if (score >= 80) return 'excellent';
    if (score >= 60) return 'good';
    if (score >= 30) return 'poor';
    return 'empty';
  }

  /**
   * NUEVA FUNCIÓN: Genera datos de prueba para testing
   */
  public static generateTestData(type: 'hotspots' | 'technical' | 'combined' = 'hotspots'): Chart3DData {
    console.log('🧪 Generating test data for type:', type);

    switch (type) {
      case 'hotspots':
        return this.generateDemoHotspotsData();
      
      case 'technical':
        return this.generateDemoTechnicalData(['geometry', 'ai_risk', 'wear']);
      
      case 'combined':
        return this.convertCombinedTo3D(
          [
            { location: 'Test KP 10', defect_count: 5, risk_score: 0.7, recommendation_priority: 'HIGH' },
            { location: 'Test KP 20', defect_count: 8, risk_score: 0.9, recommendation_priority: 'CRITICAL' }
          ],
          { location_range: { kp_start: 0, kp_end: 30 } },
          ['geometry', 'ai_risk']
        );
      
      default:
        return this.getEmptyChart3DData();
    }
  }
}